import React from 'react'
import { Box, Typography, Paper } from '@mui/material'
import { PlatinumTable } from '../platinum-table-new/PlatinumTable'
import { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { PVecType } from '../../types/carbon/models/PVecType'

/**
 * Mock table with long top header labels for visual testing of line-clamp truncation.
 *
 * Layout:
 *   Row 0: top groups (spanning all 3 data columns)
 *   Row 1: top vec labels (one per data column) — these are the long labels under test
 *   Rows 2-4: data (3 side vecs × 1 band each)
 *
 * Coordinate system (headerCols=1, no side groups):
 *   col 0 = side label column (sideThickness=0, sideLabelCols=1)
 *   col 1,2,3 = data columns → top.groups x starts at 1
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MOCK_DATA = {
  top: {
    groups: [
      // One group spanning all 3 data columns (x=1 because side label is col 0)
      { text: 'Demographic subgroups', x: 1, y: 0, w: 3, h: 1 },
    ],
    vecs: [
      {
        type: PVecType.Code,
        label: 'Male respondents aged 18–34 (core advertising target group)',
        allowPC: true,
      },
      {
        type: PVecType.Code,
        label: 'Female respondents 35–54 who regularly consume broadcast media and streaming services',
        allowPC: true,
      },
      {
        type: PVecType.Code,
        label: 'All respondents aged 55 and above regardless of media consumption habits',
        allowPC: true,
      },
    ],
  },
  side: {
    vecs: [
      { type: PVecType.Code, label: 'Sky TV', allowPC: true },
      { type: PVecType.Code, label: 'Netflix', allowPC: true },
      { type: PVecType.Code, label: 'Disney+', allowPC: true },
    ],
  },
  cells: {
    rows: [
      { cell: [{ freq: 450 }, { freq: 320 }, { freq: 230 }] },
      { cell: [{ freq: 550 }, { freq: 680 }, { freq: 770 }] },
      { cell: [{ freq: 200 }, { freq: 150 }, { freq: 300 }] },
    ],
  },
  properties: {
    top: {
      groups: { visible: true },
      labels: { visible: true },
    },
    side: {
      labels: { visible: true },
    },
    cells: {
      frequencies: true,
      columnPercents: false,
      rowPercents: false,
      percentSign: false,
      missingAsZero: false,
      missingAsBlank: false,
      zeroAsBlank: false,
      blankAsChar: false,
      blankChar: '',
      percentsAsProportions: false,
      showRedundant100: false,
    },
    decimals: {
      frequencies: 0,
      percents: 1,
      expressions: 3,
      statistics: 2,
    },
    significance: {
      appendLetters: false,
    },
  },
}

const LongTopHeaderMock: React.FC = () => {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Long Top Header Mock</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Verifies that long top header labels are truncated with line-clamp and show a tooltip on hover.
        Hover over a column header to confirm the tooltip shows the full text.
      </Typography>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
        <PlatinumTable data={MOCK_DATA as unknown as PlatinumData} />
      </Box>
    </Paper>
  )
}

export default LongTopHeaderMock
